/**
 * ModelSignalStatsPanel
 *
 * Read-only dashboard for /api/tt/admin/model-signal-stats.
 * Surfaces:
 *   1. Summary (analyzed / settled / win-rate / PnL / ROI / drawdown)
 *   2. Units saved by blocks
 *   3. ROI by guard
 *   4. ROI by SetProfile (with delta vs old MW default)
 *   5. Switch impact (old MW vs new recommended)
 *   6. Worst / best patterns
 *
 * Pure analytics — no Telegram, Policy Guard, Kelly, or pick changes.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminGet } from '../data/api';
import { RefreshCcw, Activity, AlertTriangle } from 'lucide-react';

const mono = { fontFamily: 'JetBrains Mono, monospace' };

const fmtPct = (v, d = 1) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `${(Number(v) * 100).toFixed(d)}%`;
};
const fmtEv = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
};
const fmtNum = (v, d = 2) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return Number(v).toFixed(d);
};
const signedNum = (v, d = 2) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${n.toFixed(d)}`;
};
const roiPct = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v) * 100;
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
};

const evCls = (v) =>
  v == null ? 'text-zinc-500'
  : v >= 5 ? 'text-emerald-300'
  : v >= 0 ? 'text-zinc-300'
  : 'text-rose-300';

const roiCls = (v) =>
  v == null ? 'text-zinc-500'
  : v > 0.05 ? 'text-emerald-300'
  : v > 0 ? 'text-emerald-400'
  : v >= -0.05 ? 'text-zinc-400'
  : 'text-rose-300';

const WARNING_LABEL = {
  LOW_SAMPLE: 'Sample troppo piccolo (<100 settled)',
  HIGH_ROI_LOW_SAMPLE: 'ROI alto con n<10 — non statisticamente affidabile',
  EV_CALIBRATION_RISK: 'EV positivo ma ROI negativo — mis-calibrazione probabile',
};

export default function ModelSignalStatsPanel({ pin }) {
  const [days, setDays] = useState(30);
  const [marketType, setMarketType] = useState('all');
  const [profileType, setProfileType] = useState('');
  const [guard, setGuard] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (marketType && marketType !== 'all') params.set('market_type', marketType);
      if (profileType) params.set('profile_type', profileType);
      if (guard) params.set('strongest_guard', guard);
      const r = await adminGet(`/tt/admin/model-signal-stats?${params}`, pin);
      setData(r);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [pin, days, marketType, profileType, guard]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const byGuard = data?.by_guard || [];
  const byProfile = data?.by_profile || [];
  const blocks = data?.block_impact || [];
  const sw = data?.switch_impact || {};
  const worst = data?.worst_patterns || [];
  const best = data?.best_patterns || [];
  const warnings = data?.warnings || [];

  const totalUnitsSaved = useMemo(
    () => blocks.reduce((s, b) => s + (b.units_saved || 0), 0),
    [blocks],
  );

  return (
    <div
      data-testid="model-signal-stats-panel"
      className="rounded-sm p-4 space-y-3"
      style={{ background: '#18181B', border: '1px solid #27272A', ...mono }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-emerald-400" />
          <span className="text-zinc-100 text-sm font-semibold tracking-wide">
            Model Signal Stats
          </span>
          <span className="text-[9px] text-zinc-500">
            audit · zero LLM · read-only
          </span>
        </div>
        <button
          data-testid="mss-reload-btn"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1 px-2 py-1 rounded-sm bg-zinc-800/60 border border-zinc-700 text-zinc-300 hover:text-zinc-100 disabled:opacity-50 text-[10px]"
        >
          <RefreshCcw size={10} className={loading ? 'animate-spin' : ''} />
          {loading ? 'loading' : 'reload'}
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[10px]">
        <select
          data-testid="mss-days"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="px-1.5 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200"
        >
          <option value={7}>7d</option>
          <option value={14}>14d</option>
          <option value={30}>30d</option>
          <option value={90}>90d</option>
        </select>
        <select
          data-testid="mss-market"
          value={marketType}
          onChange={(e) => setMarketType(e.target.value)}
          className="px-1.5 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200"
        >
          <option value="all">Tutti i market</option>
          <option value="mw">MW</option>
          <option value="ou">OU</option>
          <option value="handicap">Handicap</option>
        </select>
        <select
          data-testid="mss-profile"
          value={profileType}
          onChange={(e) => setProfileType(e.target.value)}
          className="px-1.5 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200"
        >
          <option value="">Tutti i profili</option>
          <option value="STOMP_CLEAN">STOMP_CLEAN</option>
          <option value="CONTROLLED_FAST">CONTROLLED_FAST</option>
          <option value="CONTROLLED_LONG">CONTROLLED_LONG</option>
          <option value="BALANCED_LONG">BALANCED_LONG</option>
          <option value="TRUE_CHAOS">TRUE_CHAOS</option>
          <option value="BALANCED">BALANCED</option>
        </select>
        <select
          data-testid="mss-guard"
          value={guard}
          onChange={(e) => setGuard(e.target.value)}
          className="px-1.5 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200"
        >
          <option value="">Tutti i guard</option>
          <option value="no_guard">no_guard</option>
          <option value="FAST_MATCH_RISK_STRONG">FAST_MATCH_RISK_STRONG</option>
          <option value="FATIGUE_COLLAPSE_RISK">FATIGUE_COLLAPSE_RISK</option>
          <option value="FATIGUE_INDUCED_OVER">FATIGUE_INDUCED_OVER</option>
          <option value="MW_FRAGILE_SWITCH_TO_OU">MW_FRAGILE_SWITCH_TO_OU</option>
          <option value="SET_SCORE_MARKET_DIVERGENCE_HARD">DIVERGENCE_HARD</option>
          <option value="OVERCONFIDENT_SETSCORE">OVERCONFIDENT_SETSCORE</option>
        </select>
      </div>

      {err && (
        <div className="text-[10px] text-rose-300 bg-rose-950/40 border border-rose-800 rounded-sm px-2 py-1">
          {err}
        </div>
      )}

      {warnings.length > 0 && (
        <div
          data-testid="mss-warnings"
          className="flex items-start gap-2 text-[10px] text-amber-200 bg-amber-950/40 border border-amber-800 rounded-sm px-2 py-1"
        >
          <AlertTriangle size={11} className="mt-0.5 shrink-0" />
          <div className="space-y-0.5">
            {warnings.map((w) => (
              <div key={w}>{WARNING_LABEL[w] || w}</div>
            ))}
          </div>
        </div>
      )}

      {/* 1. Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
        <SummaryCell label="analyzed" value={summary.analyzed} testid="mss-analyzed" />
        <SummaryCell label="settled" value={summary.settled} testid="mss-settled" />
        <SummaryCell label="win rate" value={fmtPct(summary.win_rate, 1)}
          tone={summary.win_rate >= 0.5 ? 'pos' : 'neg'} testid="mss-winrate" />
        <SummaryCell label="ROI flat 1u" value={roiPct(summary.roi_flat_1u)}
          tone={summary.roi_flat_1u > 0 ? 'pos' : 'neg'} testid="mss-roi" />
        <SummaryCell label="PnL 1u" value={signedNum(summary.pnl_flat_1u)}
          tone={summary.pnl_flat_1u > 0 ? 'pos' : 'neg'} testid="mss-pnl" />
        <SummaryCell label="avg odds" value={fmtNum(summary.avg_odds, 2)} testid="mss-avgodds" />
        <SummaryCell label="avg EV" value={fmtEv(summary.avg_ev)}
          tone={summary.avg_ev > 0 ? 'pos' : 'neg'} testid="mss-avgev" />
        <SummaryCell label="max DD" value={signedNum(-Math.abs(summary.max_drawdown || 0))}
          tone="neg" testid="mss-dd" />
      </div>

      {/* 2. Units saved by blocks */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-[10px] text-zinc-500 tracking-wider">
          <span>UNITS SAVED BY BLOCKS</span>
          <span data-testid="mss-units-saved-total" className={`text-zinc-100 font-semibold`}>
            {signedNum(totalUnitsSaved)} u
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]" data-testid="mss-block-impact-table">
            <thead>
              <tr className="text-zinc-500 border-b border-zinc-800">
                <th className="text-left py-1">rule</th>
                <th className="text-left">reason</th>
                <th className="text-right">n</th>
                <th className="text-right">settled</th>
                <th className="text-right">old W/L</th>
                <th className="text-right">old PnL</th>
                <th className="text-right">saved</th>
                <th className="text-right">avg EV</th>
              </tr>
            </thead>
            <tbody>
              {blocks.length === 0 && (
                <tr><td colSpan={8} className="text-center text-zinc-500 py-2">
                  Nessun blocco nel window.
                </td></tr>
              )}
              {blocks.map((b, i) => (
                <tr key={`${b.rule_id}-${b.block_reason}-${i}`}
                    data-testid={`mss-block-row-${i}`}
                    className="text-zinc-300 border-b border-zinc-900/50">
                  <td className="py-1 text-zinc-400">{b.rule_id}</td>
                  <td>{b.block_reason}</td>
                  <td className="text-right">{b.n_blocked}</td>
                  <td className="text-right text-zinc-500">{b.settled_blocked}</td>
                  <td className="text-right text-zinc-500">
                    {b.hypothetical_old_default_wins}/{b.hypothetical_old_default_losses}
                  </td>
                  <td className={`text-right ${b.hypothetical_old_default_pnl < 0 ? 'text-rose-300' : 'text-emerald-300'}`}>
                    {signedNum(b.hypothetical_old_default_pnl)}
                  </td>
                  <td className={`text-right font-semibold ${(b.units_saved || 0) > 0 ? 'text-emerald-300' : 'text-zinc-500'}`}>
                    {signedNum(b.units_saved)}
                  </td>
                  <td className={`text-right ${evCls(b.avg_ev_blocked)}`}>
                    {fmtEv(b.avg_ev_blocked)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 3. ROI by Guard */}
      <div className="space-y-1">
        <div className="text-[10px] text-zinc-500 tracking-wider">ROI BY GUARD</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]" data-testid="mss-by-guard-table">
            <thead>
              <tr className="text-zinc-500 border-b border-zinc-800">
                <th className="text-left py-1">guard</th>
                <th className="text-right">n</th>
                <th className="text-right">settled</th>
                <th className="text-right">W-rate</th>
                <th className="text-right">avg odds</th>
                <th className="text-right">avg EV</th>
                <th className="text-right">PnL</th>
                <th className="text-right">ROI</th>
              </tr>
            </thead>
            <tbody>
              {byGuard.map((g) => (
                <tr key={g.guard} data-testid={`mss-guard-row-${g.guard}`}
                    className="text-zinc-300 border-b border-zinc-900/50">
                  <td className="py-1">{g.guard}</td>
                  <td className="text-right">{g.n}</td>
                  <td className="text-right text-zinc-500">{g.settled}</td>
                  <td className="text-right">{fmtPct(g.win_rate)}</td>
                  <td className="text-right text-zinc-500">{fmtNum(g.avg_odds, 2)}</td>
                  <td className={`text-right ${evCls(g.avg_ev)}`}>{fmtEv(g.avg_ev)}</td>
                  <td className={`text-right ${roiCls(g.roi_flat_1u)}`}>
                    {signedNum(g.pnl_flat_1u)}
                  </td>
                  <td className={`text-right font-semibold ${roiCls(g.roi_flat_1u)}`}>
                    {roiPct(g.roi_flat_1u)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 4. ROI by SetProfile */}
      <div className="space-y-1">
        <div className="text-[10px] text-zinc-500 tracking-wider">
          ROI BY SETPROFILE · old MW vs new recommended
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]" data-testid="mss-by-profile-table">
            <thead>
              <tr className="text-zinc-500 border-b border-zinc-800">
                <th className="text-left py-1">profile</th>
                <th className="text-right">n</th>
                <th className="text-right">settled</th>
                <th className="text-right">W-rate</th>
                <th className="text-right">avg EV</th>
                <th className="text-right">old MW ROI</th>
                <th className="text-right">new ROI</th>
                <th className="text-right">Δ</th>
              </tr>
            </thead>
            <tbody>
              {byProfile.map((p) => (
                <tr key={p.profile_type}
                    data-testid={`mss-profile-row-${p.profile_type}`}
                    className="text-zinc-300 border-b border-zinc-900/50">
                  <td className="py-1">{p.profile_type}</td>
                  <td className="text-right">{p.n}</td>
                  <td className="text-right text-zinc-500">{p.settled}</td>
                  <td className="text-right">{fmtPct(p.win_rate)}</td>
                  <td className={`text-right ${evCls(p.avg_ev)}`}>{fmtEv(p.avg_ev)}</td>
                  <td className={`text-right ${roiCls(p.old_default_mw_roi)}`}>
                    {roiPct(p.old_default_mw_roi)}
                  </td>
                  <td className={`text-right ${roiCls(p.setprofile_recommended_roi)}`}>
                    {roiPct(p.setprofile_recommended_roi)}
                  </td>
                  <td className={`text-right font-semibold ${
                    p.delta_roi_vs_old_default == null ? 'text-zinc-500'
                    : p.delta_roi_vs_old_default > 0 ? 'text-emerald-300'
                    : 'text-rose-300'
                  }`}>
                    {p.delta_roi_vs_old_default == null ? '—'
                      : `${p.delta_roi_vs_old_default >= 0 ? '+' : ''}${(p.delta_roi_vs_old_default * 100).toFixed(1)}pp`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 5. Switch impact */}
      <div
        data-testid="mss-switch-impact"
        className="rounded-sm bg-zinc-950/60 border border-zinc-800 p-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]"
      >
        <div className="col-span-2 sm:col-span-4 text-zinc-500 tracking-wider">
          SWITCH IMPACT · old MW vs new recommendation
        </div>
        <Cell label="switched cases" value={sw.switched_cases ?? 0} />
        <Cell label="settled switched" value={sw.settled_switched ?? 0} />
        <Cell label="old MW ROI" value={roiPct(sw.old_default_mw_roi)}
          tone={sw.old_default_mw_roi >= 0 ? 'pos' : 'neg'} />
        <Cell label="new ROI" value={roiPct(sw.new_recommended_market_roi)}
          tone={sw.new_recommended_market_roi >= 0 ? 'pos' : 'neg'} />
        <Cell label="Δ ROI"
          value={sw.delta_roi == null ? '—'
            : `${sw.delta_roi >= 0 ? '+' : ''}${(sw.delta_roi * 100).toFixed(1)}pp`}
          tone={sw.delta_roi >= 0 ? 'pos' : 'neg'}
          testid="mss-switch-delta" />
        <Cell label="→ Over" value={sw.switched_to_over ?? 0} />
        <Cell label="→ Under" value={sw.switched_to_under ?? 0} />
        <Cell label="→ HC" value={sw.switched_to_hc ?? 0} />
      </div>

      {/* 6. Best / worst patterns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <PatternsTable title="WORST PATTERNS · ROI ascending" rows={worst} testid="mss-worst" />
        <PatternsTable title="BEST PATTERNS · ROI descending" rows={best} testid="mss-best" />
      </div>

      <div className="text-[9px] text-zinc-600">
        window {data?.window_days || days}d · since{' '}
        {data?.since ? new Date(data.since).toLocaleString('it-IT') : '—'} ·
        analytics-only · no Telegram, Policy Guard, Kelly changes
      </div>
    </div>
  );
}

function SummaryCell({ label, value, tone, testid }) {
  const cls = tone === 'pos' ? 'text-emerald-300'
    : tone === 'neg' ? 'text-rose-300' : 'text-zinc-100';
  return (
    <div data-testid={testid}
         className="rounded-sm bg-zinc-950/60 border border-zinc-800 p-1.5">
      <div className="text-[9px] text-zinc-500 tracking-wider">{label}</div>
      <div className={`${cls} font-semibold`}>
        {value === undefined || value === null ? '—' : value}
      </div>
    </div>
  );
}

function Cell({ label, value, tone, testid }) {
  const cls = tone === 'pos' ? 'text-emerald-300'
    : tone === 'neg' ? 'text-rose-300' : 'text-zinc-100';
  return (
    <div data-testid={testid}
         className="rounded-sm bg-zinc-900/40 border border-zinc-800 p-1.5">
      <div className="text-[9px] text-zinc-500">{label}</div>
      <div className={`${cls} font-semibold`}>{value}</div>
    </div>
  );
}

function PatternsTable({ title, rows, testid }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] text-zinc-500 tracking-wider">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px]" data-testid={testid}>
          <thead>
            <tr className="text-zinc-500 border-b border-zinc-800">
              <th className="text-left py-1">pattern</th>
              <th className="text-right">n</th>
              <th className="text-right">W%</th>
              <th className="text-right">ROI</th>
              <th className="text-right">PnL</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).length === 0 && (
              <tr><td colSpan={5} className="text-center text-zinc-500 py-2">—</td></tr>
            )}
            {(rows || []).map((p, i) => (
              <tr key={p.pattern_key} className="text-zinc-300 border-b border-zinc-900/50"
                  data-testid={`${testid}-row-${i}`}>
                <td className="py-1 truncate max-w-[18rem]" title={p.pattern_key}>
                  {p.pattern_key}
                </td>
                <td className="text-right">{p.n}</td>
                <td className="text-right">{fmtPct(p.win_rate)}</td>
                <td className={`text-right font-semibold ${roiCls(p.roi_flat_1u)}`}>
                  {roiPct(p.roi_flat_1u)}
                </td>
                <td className={`text-right ${roiCls(p.roi_flat_1u)}`}>
                  {signedNum(p.pnl_flat_1u)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
